
import React from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface CommentVoteBarProps {
  commentId: string;
  upvoteCount: number;
  downvoteCount: number;
  maxVotes?: number;
  hasUpvoted?: boolean;
  hasDownvoted?: boolean;
  isVoting?: boolean;
  onVote: (commentId: string, voteType: 'up' | 'down') => void;
  size?: 'sm' | 'md' | 'lg';
}

const CommentVoteBar: React.FC<CommentVoteBarProps> = ({
  commentId,
  upvoteCount,
  downvoteCount,
  maxVotes = 1,
  hasUpvoted = false,
  hasDownvoted = false,
  isVoting = false,
  onVote,
  size = 'md'
}) => {
  // Calculate the ratio for the balance bar
  const totalVotes = upvoteCount + downvoteCount;
  const upvoteRatio = totalVotes > 0 ? (upvoteCount / totalVotes) : 0.5;
  
  // Define button sizes based on the size prop
  const iconSize = {
    sm: 12,
    md: 14,
    lg: 16
  }[size];
  
  const buttonStyles = {
    sm: 'h-5 px-0.5',
    md: 'h-6 px-1.5',
    lg: 'h-8 px-2'
  }[size];
  
  const textStyles = {
    sm: 'text-xs',
    md: 'text-xs',
    lg: 'text-sm'
  }[size];
  
  return (
    <div className="flex items-center space-x-1">
      <Button
        variant="ghost"
        size="sm"
        disabled={isVoting}
        onClick={() => onVote(commentId, 'up')}
        className={cn(
          buttonStyles,
          hasUpvoted && 'text-blue-600 dark:text-blue-400'
        )}
      >
        <ThumbsUp className={cn("mr-1", hasUpvoted ? "fill-current" : "")} width={iconSize} height={iconSize} />
        <span className={cn(textStyles, "font-normal")}>{upvoteCount || ''}</span>
      </Button>
      
      <Button
        variant="ghost"
        size="sm"
        disabled={isVoting}
        onClick={() => onVote(commentId, 'down')}
        className={cn(
          buttonStyles,
          hasDownvoted && 'text-red-600 dark:text-red-400'
        )}
      >
        <ThumbsDown className={cn("mr-1", hasDownvoted ? "fill-current" : "")} width={iconSize} height={iconSize} />
        <span className={cn(textStyles, "font-normal")}>{downvoteCount || ''}</span>
      </Button>
      
      {/* Balance bar visualization - only shown when there are votes */}
      {totalVotes > 0 && maxVotes > 0 && (
        <div className="flex-1 max-w-[60px] h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mx-1">
          <div 
            className="h-full bg-blue-500 dark:bg-blue-600 rounded-full"
            style={{ width: `${upvoteRatio * 100}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default CommentVoteBar;
